/**
 * Admin Moderation Dashboard.
 *
 * Access is enforced BY THE BACKEND (`require_admin` on every /api/admin/*
 * endpoint). This page is a convenience: it never grants access on its
 * own — a hostile user who navigates to /admin/moderation directly will
 * just see empty state + 403 responses. `data-testid` attributes are used
 * so the backend authorisation can be independently verified in tests.
 *
 * Web-only surface — the mobile app's normal navigation deliberately has
 * no link here. Admins can still reach it by typing the URL if they must.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Eye, EyeOff, CheckCircle, XCircle, Clock, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../lib/authFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  open: { label: 'Open', color: 'text-[#C6A75E]', icon: Clock },
  under_review: { label: 'Under review', color: 'text-[#8BAA9E]', icon: Eye },
  action_taken: { label: 'Action taken', color: 'text-[#B85555]', icon: CheckCircle },
  dismissed: { label: 'Dismissed', color: 'text-[#7A7670]', icon: XCircle },
};

const REASON_LABELS = {
  spam: 'Spam',
  harassment: 'Harassment',
  hate_speech: 'Hate speech',
  self_harm: 'Self-harm',
  sexual_content: 'Sexual content',
  misinformation: 'Misinformation',
  other: 'Other',
};

const AdminModeration = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [detail, setDetail] = useState(null); // full review pane
  const [pending, setPending] = useState(null); // { action, post_id }
  const [note, setNote] = useState('');
  const [confirmModal, setConfirmModal] = useState(null); // { action, post_id, label }

  // Verify admin role via backend — never trust the frontend.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      try {
        const r = await authFetch(`${API_URL}/api/admin/moderation/me`);
        const d = await r.json();
        if (!cancelled) setIsAdmin(!!d.is_admin);
      } catch { /* noop */ }
      finally { if (!cancelled) setChecking(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await authFetch(`${API_URL}/api/admin/moderation/summary`);
      if (r.ok) setSummary(await r.json());
    } catch { /* noop */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (reasonFilter) params.set('reason', reasonFilter);
      const r = await authFetch(`${API_URL}/api/admin/moderation/reports?${params}`);
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
      }
    } finally {
      setLoadingItems(false);
    }
  }, [statusFilter, reasonFilter]);

  useEffect(() => {
    if (isAdmin) { loadSummary(); loadItems(); }
  }, [isAdmin, loadSummary, loadItems]);

  const openDetail = async (post_id) => {
    try {
      const r = await authFetch(`${API_URL}/api/admin/moderation/posts/${post_id}`);
      if (r.ok) {
        const d = await r.json();
        setDetail(d);
        setNote(d.post?.moderator_note || '');
      } else {
        toast.error('Could not load post details.');
      }
    } catch {
      toast.error('Could not load post details.');
    }
  };

  const doAction = async (post_id, action) => {
    setPending({ action, post_id });
    try {
      const r = await authFetch(`${API_URL}/api/admin/moderation/posts/${post_id}/${action}`, {
        method: 'POST',
      });
      if (r.ok) {
        toast.success(
          action === 'hide' ? 'Post hidden.' :
          action === 'restore' ? 'Post restored.' :
          action === 'dismiss' ? 'Reports dismissed.' : 'Done.',
        );
        setDetail(null);
        await Promise.all([loadSummary(), loadItems()]);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err?.detail || "That action didn't complete. Please try again.");
      }
    } finally {
      setPending(null);
      setConfirmModal(null);
    }
  };

  const saveNote = async () => {
    if (!detail?.post?.post_id || !note.trim()) return;
    setPending({ action: 'note', post_id: detail.post.post_id });
    try {
      const r = await authFetch(`${API_URL}/api/admin/moderation/posts/${detail.post.post_id}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.trim() }),
      });
      if (r.ok) {
        toast.success('Note saved.');
        openDetail(detail.post.post_id);
      } else {
        toast.error("Couldn't save note.");
      }
    } finally {
      setPending(null);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#8BAA9E] animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center px-6" data-testid="admin-forbidden">
        <div className="max-w-sm text-center">
          <Shield className="w-10 h-10 text-[#7A7670] mx-auto mb-4" />
          <p className="text-[#DCD3CA] font-serif text-xl mb-2">Not available</p>
          <p className="text-[#7A7670] text-sm font-sans mb-6">This area is reserved for Future Self administrators.</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full bg-[#252528] text-[#DCD3CA] border border-white/10">Back to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E] text-[#DCD3CA]" data-testid="admin-dashboard">
      <header className="px-6 pt-8 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center" aria-label="Back to dashboard">
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <div className="flex-1">
          <p className="text-[#8BAA9E] text-xs tracking-[0.2em] uppercase font-sans mb-1">Admin</p>
          <h1 className="font-serif text-2xl">Moderation</h1>
        </div>
      </header>

      {summary && (
        <section className="px-6 mb-6 grid grid-cols-2 gap-3" data-testid="admin-summary">
          <SummaryCard label="Open reports" value={summary.open_reports} testId="sum-open-reports" />
          <SummaryCard label="Posts with open reports" value={summary.posts_with_open_reports} testId="sum-posts-open" />
          <SummaryCard label="Auto-hidden posts" value={summary.auto_hidden_posts} testId="sum-auto-hidden" />
          <SummaryCard label="Resolved" value={summary.resolved_posts} testId="sum-resolved" />
        </section>
      )}

      <section className="px-6 mb-4 flex flex-wrap gap-2">
        <FilterPill label="All" active={!statusFilter} onClick={() => setStatusFilter('')} />
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <FilterPill key={k} label={v.label} active={statusFilter === k} onClick={() => setStatusFilter(k)} data-testid={`filter-status-${k}`} />
        ))}
      </section>
      <section className="px-6 mb-4 flex flex-wrap gap-2">
        <FilterPill label="Any reason" active={!reasonFilter} onClick={() => setReasonFilter('')} />
        {Object.entries(REASON_LABELS).map(([k, v]) => (
          <FilterPill key={k} label={v} active={reasonFilter === k} onClick={() => setReasonFilter(k)} data-testid={`filter-reason-${k}`} />
        ))}
      </section>

      <section className="px-6 pb-24 space-y-3" data-testid="admin-items">
        {loadingItems ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#8BAA9E]" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[#7A7670] text-sm font-sans">Nothing to review right now.</div>
        ) : items.map((item) => {
          const S = STATUS_LABELS[item.moderation_status] || STATUS_LABELS.open;
          const Icon = S.icon;
          return (
            <button
              key={item.post_id}
              onClick={() => openDetail(item.post_id)}
              className="w-full text-left bg-[#252528] border border-white/5 rounded-2xl p-4 hover:border-white/15 transition"
              data-testid={`admin-item-${item.post_id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-1 text-xs font-sans ${S.color}`}>
                  <Icon className="w-3.5 h-3.5" />{S.label}
                </span>
                <span className="text-xs text-[#7A7670]">{item.total_reports} report{item.total_reports === 1 ? '' : 's'} · {item.unique_reporters} reporter{item.unique_reporters === 1 ? '' : 's'}</span>
              </div>
              <p className="text-[#DCD3CA] text-sm font-sans line-clamp-2 mb-2">{item.content}</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(item.reason_counts).map(([r, c]) => (
                  <span key={r} className="text-[10px] text-[#7A7670] bg-white/5 rounded-full px-2 py-0.5">{REASON_LABELS[r] || r} × {c}</span>
                ))}
              </div>
            </button>
          );
        })}
      </section>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg bg-[#1C1C1E] border-white/10 text-[#DCD3CA]" data-testid="admin-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-lg">Report review</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="bg-[#252528] border border-white/5 rounded-2xl p-4">
                  <p className="text-[#DCD3CA] whitespace-pre-wrap font-sans" data-testid="admin-detail-content">{detail.post.content}</p>
                  <p className="text-[10px] text-[#7A7670] mt-2 font-sans">Posted {new Date(detail.post.created_at).toLocaleString()}</p>
                </div>
                <div className="text-xs text-[#B5B1AB] font-sans flex flex-wrap gap-3">
                  <span>Reports: <strong>{detail.total_reports}</strong></span>
                  <span>Unique reporters: <strong>{detail.unique_reporters}</strong></span>
                  <span>Prior actions on author: <strong>{detail.author_prior_actioned_posts}</strong></span>
                </div>
                <div className="text-xs text-[#B5B1AB] font-sans">
                  Reasons: {Object.entries(detail.reason_counts).map(([r, c]) => `${REASON_LABELS[r] || r} × ${c}`).join(' · ') || 'None'}
                </div>
                <div>
                  <label className="text-xs text-[#7A7670] font-sans">Private moderator note</label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 bg-[#252528] border-white/10 text-[#DCD3CA]" data-testid="admin-note-input" />
                  <Button type="button" onClick={saveNote} variant="outline" className="mt-2 border-white/10 text-xs h-7 rounded-full" data-testid="admin-save-note-btn">
                    {pending?.action === 'note' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />} Save note
                  </Button>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {detail.post.is_visible ? (
                  <Button
                    onClick={() => setConfirmModal({ action: 'hide', post_id: detail.post.post_id, label: 'Hide this post' })}
                    className="w-full rounded-full bg-[#B85555] hover:bg-[#a04444] text-white"
                    data-testid="admin-hide-btn"
                    disabled={pending !== null}
                  >
                    <EyeOff className="w-4 h-4 mr-2" />Hide post
                  </Button>
                ) : (
                  <Button
                    onClick={() => setConfirmModal({ action: 'restore', post_id: detail.post.post_id, label: 'Restore this post' })}
                    className="w-full rounded-full bg-[#8BAA9E] hover:brightness-110 text-[#1C1C1E]"
                    data-testid="admin-restore-btn"
                    disabled={pending !== null}
                  >
                    <Eye className="w-4 h-4 mr-2" />Restore post
                  </Button>
                )}
                <Button
                  onClick={() => doAction(detail.post.post_id, 'dismiss')}
                  variant="outline"
                  className="w-full rounded-full border-white/10"
                  data-testid="admin-dismiss-btn"
                  disabled={pending !== null}
                >
                  Dismiss reports
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation for destructive actions */}
      <Dialog open={!!confirmModal} onOpenChange={(o) => !o && setConfirmModal(null)}>
        <DialogContent className="max-w-sm bg-[#1C1C1E] border-white/10 text-[#DCD3CA]" data-testid="admin-confirm-modal">
          {confirmModal && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-lg">{confirmModal.label}?</DialogTitle></DialogHeader>
              <p className="text-sm text-[#B5B1AB] font-sans">This action is logged in the moderation audit trail.</p>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setConfirmModal(null)} className="w-full rounded-full border-white/10">Cancel</Button>
                <Button
                  onClick={() => doAction(confirmModal.post_id, confirmModal.action)}
                  className="w-full rounded-full bg-[#B85555] hover:bg-[#a04444] text-white"
                  data-testid="admin-confirm-btn"
                  disabled={pending !== null}
                >
                  {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryCard = ({ label, value, testId }) => (
  <div className="bg-[#252528] border border-white/5 rounded-2xl p-4" data-testid={testId}>
    <p className="text-2xl font-serif text-[#DCD3CA]">{value ?? 0}</p>
    <p className="text-xs text-[#7A7670] font-sans mt-1">{label}</p>
  </div>
);

const FilterPill = ({ label, active, onClick, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-sans border ${active ? 'bg-[#8BAA9E]/15 border-[#8BAA9E]/40 text-[#8BAA9E]' : 'bg-transparent border-white/10 text-[#B5B1AB]'}`}
    {...rest}
  >
    {label}
  </button>
);

export default AdminModeration;
